import { z } from 'zod';
import { baseMessageSchema, createMessageId } from './_base';
import { invitationUnion } from './invitation';
import { matchmakingPlayerSchema, matchmakingPreferencesSchema } from './shared';

export enum MatchmakingMessageType {
  // Host messages
  MATCHMAKER_HOST_CONFIG = 'MATCHMAKER_HOST_CONFIG',

  // Player/Invitation messages
  MATCHMAKER_CREATE_INVITATION = 'MATCHMAKER_CREATE_INVITATION',
  MATCHMAKER_JOIN_INVITATION = 'MATCHMAKER_JOIN_INVITATION',
  MATCHMAKER_LEAVE_INVITATION = 'MATCHMAKER_LEAVE_INVITATION',
  MATCHMAKER_UPDATE_PREFERENCES = 'MATCHMAKER_UPDATE_PREFERENCES',
  MATCHMAKER_ADD_TO_QUEUE = 'MATCHMAKER_ADD_TO_QUEUE',
  MATCHMAKER_UPDATE_INVITATION = 'MATCHMAKER_UPDATE_INVITATION',
  MATCHMAKER_KICK_PLAYER = 'MATCHMAKER_KICK_PLAYER',
  MATCHMAKER_INVALIDATE_SESSION = 'MATCHMAKER_INVALIDATE_SESSION',

  // Soft kick activation messages
  MATCHMAKER_ACTIVATE_SOFT_KICK_REPLY = 'MATCHMAKER_ACTIVATE_SOFT_KICK_REPLY',
  MATCHMAKER_ACTIVATE_SOFT_KICK_BY_SESSION_ID = 'MATCHMAKER_ACTIVATE_SOFT_KICK_BY_SESSION_ID',

  // Realtime API response messages
  MATCHMAKER_INVITATION_CREATED = 'MATCHMAKER_INVITATION_CREATED',
  MATCHMAKER_INVITATION_UPDATED = 'MATCHMAKER_INVITATION_UPDATED',
  MATCHMAKER_MATCH_FOUND = 'MATCHMAKER_MATCH_FOUND',
  MATCHMAKER_MATCH_READY = 'MATCHMAKER_MATCH_READY',
  MATCHMAKER_MATCH_FAILED = 'MATCHMAKER_MATCH_FAILED',
  MATCHMAKER_QUEUE_STATUS = 'MATCHMAKER_QUEUE_STATUS',
  MATCHMAKER_PLAYER_KICKED = 'MATCHMAKER_PLAYER_KICKED',

  // Host notification messages
  MATCHMAKER_HOST_MATCH_FOUND = 'MATCHMAKER_HOST_MATCH_FOUND',

  // Host-to-Player messages (after match)
  MATCHMAKER_HOST_READY = 'MATCHMAKER_HOST_READY',
  MATCHMAKER_CONNECTION_INFO = 'MATCHMAKER_CONNECTION_INFO',

  // Shared/System messages
  MATCHMAKER_ERROR = 'MATCHMAKER_ERROR',
  MATCHMAKER_STATE_UPDATE = 'MATCHMAKER_STATE_UPDATE',

  // Campaign switching (capacity management)
  MATCHMAKER_CAMPAIGN_SWITCH = 'MATCHMAKER_CAMPAIGN_SWITCH',
  MATCHMAKER_CAMPAIGN_SWITCH_FAILURE = 'MATCHMAKER_CAMPAIGN_SWITCH_FAILURE',
}

// Common schemas
const hostBandwidthSchema = z.object({
  uploadMbps: z.number(),
  downloadMbps: z.number(),
  lastUpdated: z.number(),
});

// const hostMachineInfoSchema = z.object({
//   cpu: z.string(),
//   gpu: z.string(),
//   ram: z.number(),
//   storage: z.number(),
// });

/**
 * Host game state for multi-campaign support
 * - idle: No game running, ready to launch any installed game
 * - prewarming: Game is launching in background (not yet ready for players)
 * - ready: Game is running and ready to accept players
 * - active: Currently in a session with a player
 * - cooldown: Game running but campaign-configured cooldown is active (host suppresses matchmaking)
 */
export const hostGameStateSchema = z.enum(['idle', 'prewarming', 'ready', 'active', 'cooldown']);

/**
 * Host coordinates for admin dashboard and debugging
 */
const hostCoordsSchema = z.object({
  lat: z.number(),
  lng: z.number(),
});

/**
 * Host Operational State (Host Authority)
 *
 * This is what the host reports about its current operational status.
 * The host owns this data and is the source of truth.
 */
// Reasons a host self-marks `matchmakeable: false`. `null`/`undefined` = healthy.
export const matchmakeableBlockReasonSchema = z.enum([
  'shadowcam_unhealthy',
  'audiocaster_missing',
  'host_audio_unhealthy',
  'edition',
  'ad_mode',
  'steam_offline',
  'global_toggle_off',
  'game_not_ready',
  'game_save_restore',
  'no_campaign',
  'admin_session_active',
  // Terminal state: admin exited with a destructive action (reboot / exit Nonsole).
  // Host must not re-enter matchmaking until PlaycastService completes the exit.
  'host_exiting',
  'active_match_override',
  'cooldown',
  // Nonsole sanitization gating (publish-and-gate)
  'virtual_display_unhealthy',
  'physical_isolation_unhealthy',
  'process_policy_violation',
  'sanitization_failure',
]);

export type MatchmakeableBlockReason = z.infer<typeof matchmakeableBlockReasonSchema>;

const hostOperationalStateSchema = z.object({
  // Operational flags
  globalMatchmakingToggle: z.boolean(), // Manual override for matchmaking
  matchmakeable: z.boolean(), // Decided by the host programatically
  matchmakeableBlockReason: matchmakeableBlockReasonSchema.nullable().optional(), // Why the host is not matchmakeable (admin visibility)
  /** Sanitization failure code when blocked by Nonsole sanitization gating (beta hosts). */
  sanitizationFailureCode: z.string().optional(),
  /** Human-readable sanitization failure detail from PlaycastService. */
  sanitizationFailureDetail: z.string().optional(),
  /** Structured host-audio hide failure sub-reason when audio hiding failed at entry. */
  hostAudioHideFailureReason: z.string().optional(),
  /** Observe-only runtime process-policy deny-list kill count (Nonsole beta). */
  processPolicyViolationCount: z.number().int().nonnegative().optional(),
  lastBlockedProcessName: z.string().optional(),
  lastBlockedReason: z.string().optional(),
  bandwidth: hostBandwidthSchema,
  guestAccount: z.boolean(),
  lastUpdated: z.number().optional(),
  currentMatchInvitationId: z.string().optional(), // Track which invitation this host is matched with

  // Current campaign & game state (what host is actually running)
  campaignId: z.string().optional(), // Which campaign this host is currently serving (e.g., 'shades')
  gameState: hostGameStateSchema.optional(), // Host lifecycle state: 'idle' | 'prewarming' | 'ready' | 'active' | 'cooldown'
  gameStateChangedAt: z.number().optional(), // Timestamp when gameState last changed (for dashboard duration display)
  lastSwitchedToCampaign: z.number().optional(), // When host completed switch TO current campaign

  // Wall-clock end of an active matchmaking cooldown (host-side authority).
  // Present only when gameState === 'cooldown'. Informational for dashboards; v1 enforcement is host-side.
  cooldownEndsAt: z.number().optional(),

  // Host type classification
  isCloudHost: z.boolean().optional(), // true = Playcast cloud host, false/undefined = wild host

  // NOTE: Geolocation (geohash, coords) moved to HostPresence
  // Hosts send geolocation via PRESENCE_UPDATE, not HOST_CONFIG
  // This breaks the circular dependency where hosts needed campaigns before sending config
  
  // NOTE: Gaming score is server-computed via SUBMIT_SCORING_METRICS → GamingScoreCoordinator.
  // Matchmaker reads from gaming_score:{machineId} Redis key (written by GamingScorePersistenceManager).
});

/**
 * Host Orchestration State (API Authority)
 *
 * This is what the API manages for orchestrating campaign switches.
 * The API owns this data and is the source of truth.
 * Hosts DO NOT send these fields in HOST_CONFIG messages.
 */
const hostOrchestrationStateSchema = z.object({
  desiredCampaign: z.string().optional(), // Target campaign for capacity switching (API sets this)
  switchRequestedAt: z.number().optional(), // When capacity manager requested a switch (API sets this)
});

/**
 * Combined Host Matchmaking State (Internal API Use Only)
 *
 * This merges operational state (from host) with orchestration state (from API).
 * Used internally by the API for matchmaking logic.
 */
const hostMatchmakingStateSchema = hostOperationalStateSchema.merge(hostOrchestrationStateSchema);

export const matchmakingSessionPresetSchema = z.object({
  description: z.string().optional(),
  authRequired: z.boolean(),
  defaultPlayerSettings: z.object({
    gamepad: z.boolean(),
    keyboard: z.boolean(),
    mouse: z.boolean(),
  }),
});

// Payload schemas for Host messages
const matchmakerHostConfigPayloadSchema = z.object({
  machineId: z.string().min(1, 'machineId cannot be empty'),
  state: hostOperationalStateSchema, // Host only sends operational state
});

// Payload schemas for Player/Invitation messages
const matchmakerCreateInvitationPayloadSchema = z.object({
  playerInfo: matchmakingPlayerSchema,
  sessionPreset: matchmakingSessionPresetSchema,
  matchmakeToPublicMachine: z.boolean(),

  // Campaign targeting (which game to play)
  campaignId: z.string().optional(), // e.g., 'shades' - if not set, matches any available host

  // Direct machine targeting (admin/testing)
  targetMachineId: z.string().optional(), // Bypass geo search, match specific machine by ID

  // Custom search pattern (see SEARCH_PATTERN_SPECIFICATION.md)
  // Default: ['W:L3', 'W:L2:1x3', 'C:L3', 'C:L2:1x3']
  searchPattern: z.array(z.string()).optional(),
});

const matchmakerJoinInvitationPayloadSchema = z.object({
  invitationId: z.string(),
  playerInfo: matchmakingPlayerSchema,
});

const matchmakerLeaveInvitationPayloadSchema = z.object({
  invitationId: z.string(),
  playerId: z.string(),
});

const matchmakerUpdatePreferencesPayloadSchema = z.object({
  invitationId: z.string(),
  preferences: matchmakingPreferencesSchema,
});

const matchmakerAddToQueuePayloadSchema = z.object({
  invitationId: z.string(),
  useSteamGuestLogin: z.boolean().optional(),
  regionOverride: z.string().optional(),
});

const matchmakerKickPlayerPayloadSchema = z.object({
  invitationId: z.string(),
  playerId: z.string(),
  reason: z.string().optional(),
});

const matchmakerInvalidateSessionPayloadSchema = z.object({
  invitationId: z.string(),
  reason: z.string().optional(),
});

// Soft kick activation payload schema
const matchmakerActivateSoftKickPayloadSchema = z.object({
  invitationId: z.string().optional(), // Optional - host will resolve active invitation
  token: z.string().optional(), // Optional - for future validation
});

// Soft kick activation by session ID payload schema
const matchmakerActivateSoftKickBySessionIdPayloadSchema = z.object({
  hostSessionId: z.string(), // Public-facing host session ID
  token: z.string().optional(), // Optional - for future validation
});

// Payload schemas for Realtime API response messages
const matchmakerInvitationCreatedPayloadSchema = z.object({
  invitationId: z.string(),
  invitation: invitationUnion.optional(),
});

const matchmakerInvitationUpdatedPayloadSchema = z.object({
  invitationId: z.string(),
  invitation: invitationUnion.optional(),
  players: z.array(matchmakingPlayerSchema),
});

const matchmakerMatchFoundPayloadSchema = z.object({
  invitationId: z.string(),
  hostBroadcastId: z.string().optional(),
  estimatedSetupTimeSeconds: z.number().optional(),
});

const matchmakerMatchReadyPayloadSchema = z.object({
  invitationId: z.string(),
  broadcastId: z.string().optional(),
});

const matchmakerMatchFailedPayloadSchema = z.object({
  invitationId: z.string(),
  reason: z.string(),
  canRetry: z.boolean(),
});

const matchmakerQueueStatusPayloadSchema = z.object({
  invitationId: z.string(),
  position: z.number(),
  estimatedWaitTimeSeconds: z.number().optional(),
  playersInQueue: z.number().optional(),
  hostsAvailable: z.number().optional(),
  noHostsAvailable: z.boolean().optional(), // True when no immediate match found
});

const matchmakerPlayerKickedPayloadSchema = z.object({
  invitationId: z.string(),
  playerId: z.string(),
  reason: z.string().optional(),
  acknowledged: z.boolean(),
});

// Payload schemas for Host-to-Player messages
const matchmakerHostReadyPayloadSchema = z.object({
  invitationId: z.string(),
  hostPeerId: z.string(),
});

const matchmakerConnectionInfoPayloadSchema = z.object({
  invitationId: z.string(),
  connectionDetails: z.record(z.string(), z.any()),
});

// Payload schemas for Shared/System messages
const matchmakerErrorPayloadSchema = z.object({
  invitationId: z.string().optional(),
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});

const matchmakerStateUpdateDetailsSchema = z.object({
  reason: z.string().optional(),
  currentMatchInvitationId: z.string().optional(),
  matchmakeable: z.boolean().optional(),
  timestamp: z.number().optional(),
  campaignId: z.string().optional(),
  isCloudHost: z.boolean().optional(),
  preferences: matchmakingPreferencesSchema.optional(),
  // NOTE: Geohash removed - now stored in HostPresence (sent via PRESENCE_UPDATE)
});

const matchmakerStateUpdatePayloadSchema = z.object({
  invitationId: z.string().optional(),
  state: z.enum(['MATCHMAKING_DISABLED', 'REGISTERED', 'PREFERENCES_UPDATED', 'IDLE']),
  stateDetails: matchmakerStateUpdateDetailsSchema.optional(),
});

// Add payload schema for host match notification
const matchmakerHostMatchFoundPayloadSchema = z.object({
  invitationId: z.string(),
  invitation: invitationUnion,
  matchTimestamp: z.number(),
  steamCredentials: z
    .array(
      z.object({
        playerId: z.string(),
        username: z.string(),
        password: z.string(),
      }),
    )
    .optional(),
});

// Add new schema for invitation update
const matchmakerUpdateInvitationPayloadSchema = z.object({
  invitationId: z.string(),
  invalid: z.boolean().optional(),
  invalidationReason: z.string().optional(),
  name: z.string().optional(),
  steamId: z.string().optional(),
});

// Campaign switch payload schema (for capacity management)
const matchmakerCampaignSwitchPayloadSchema = z.object({
  campaignId: z.string(), // Target campaign to switch to
  reason: z.string(), // Why the switch is happening (e.g., 'capacity_rebalance')
});

// Campaign switch failure payload schema (host reports failure to API)
const matchmakerCampaignSwitchFailurePayloadSchema = z.object({
  campaignId: z.string(), // Campaign that failed to launch
  reason: z.string(), // Failure reason from Playjector or timeout (e.g., 'launcher disabled', 'not authenticated', 'timeout', 'host_busy')
  machineName: z.string().optional(), // Machine name for better UX
  transient: z.boolean().optional(), // If true, don't record as permanent failure (e.g., host busy, temporary condition)
});

// Full message schemas
const matchmakerHostConfigSchema = baseMessageSchema
  .extend({
    type: z.literal(MatchmakingMessageType.MATCHMAKER_HOST_CONFIG),
  })
  .merge(matchmakerHostConfigPayloadSchema);

const matchmakerCreateInvitationSchema = baseMessageSchema
  .extend({
    type: z.literal(MatchmakingMessageType.MATCHMAKER_CREATE_INVITATION),
  })
  .merge(matchmakerCreateInvitationPayloadSchema);

const matchmakerJoinInvitationSchema = baseMessageSchema
  .extend({
    type: z.literal(MatchmakingMessageType.MATCHMAKER_JOIN_INVITATION),
  })
  .merge(matchmakerJoinInvitationPayloadSchema);

const matchmakerLeaveInvitationSchema = baseMessageSchema
  .extend({
    type: z.literal(MatchmakingMessageType.MATCHMAKER_LEAVE_INVITATION),
  })
  .merge(matchmakerLeaveInvitationPayloadSchema);

const matchmakerUpdatePreferencesSchema = baseMessageSchema
  .extend({
    type: z.literal(MatchmakingMessageType.MATCHMAKER_UPDATE_PREFERENCES),
  })
  .merge(matchmakerUpdatePreferencesPayloadSchema);

const matchmakerAddToQueueSchema = baseMessageSchema
  .extend({
    type: z.literal(MatchmakingMessageType.MATCHMAKER_ADD_TO_QUEUE),
  })
  .merge(matchmakerAddToQueuePayloadSchema);

const matchmakerUpdateInvitationSchema = baseMessageSchema
  .extend({
    type: z.literal(MatchmakingMessageType.MATCHMAKER_UPDATE_INVITATION),
  })
  .merge(matchmakerUpdateInvitationPayloadSchema);

const matchmakerKickPlayerSchema = baseMessageSchema
  .extend({
    type: z.literal(MatchmakingMessageType.MATCHMAKER_KICK_PLAYER),
  })
  .merge(matchmakerKickPlayerPayloadSchema);

const matchmakerInvalidateSessionSchema = baseMessageSchema
  .extend({
    type: z.literal(MatchmakingMessageType.MATCHMAKER_INVALIDATE_SESSION),
  })
  .merge(matchmakerInvalidateSessionPayloadSchema);

const matchmakerInvitationCreatedSchema = baseMessageSchema
  .extend({
    type: z.literal(MatchmakingMessageType.MATCHMAKER_INVITATION_CREATED),
  })
  .merge(matchmakerInvitationCreatedPayloadSchema);

const matchmakerInvitationUpdatedSchema = baseMessageSchema
  .extend({
    type: z.literal(MatchmakingMessageType.MATCHMAKER_INVITATION_UPDATED),
  })
  .merge(matchmakerInvitationUpdatedPayloadSchema);

const matchmakerMatchFoundSchema = baseMessageSchema
  .extend({
    type: z.literal(MatchmakingMessageType.MATCHMAKER_MATCH_FOUND),
  })
  .merge(matchmakerMatchFoundPayloadSchema);

const matchmakerMatchReadySchema = baseMessageSchema
  .extend({
    type: z.literal(MatchmakingMessageType.MATCHMAKER_MATCH_READY),
  })
  .merge(matchmakerMatchReadyPayloadSchema);

const matchmakerMatchFailedSchema = baseMessageSchema
  .extend({
    type: z.literal(MatchmakingMessageType.MATCHMAKER_MATCH_FAILED),
  })
  .merge(matchmakerMatchFailedPayloadSchema);

const matchmakerQueueStatusSchema = baseMessageSchema
  .extend({
    type: z.literal(MatchmakingMessageType.MATCHMAKER_QUEUE_STATUS),
  })
  .merge(matchmakerQueueStatusPayloadSchema);

const matchmakerPlayerKickedSchema = baseMessageSchema
  .extend({
    type: z.literal(MatchmakingMessageType.MATCHMAKER_PLAYER_KICKED),
  })
  .merge(matchmakerPlayerKickedPayloadSchema);

const matchmakerHostReadySchema = baseMessageSchema
  .extend({
    type: z.literal(MatchmakingMessageType.MATCHMAKER_HOST_READY),
  })
  .merge(matchmakerHostReadyPayloadSchema);

const matchmakerConnectionInfoSchema = baseMessageSchema
  .extend({
    type: z.literal(MatchmakingMessageType.MATCHMAKER_CONNECTION_INFO),
  })
  .merge(matchmakerConnectionInfoPayloadSchema);

const matchmakerErrorSchema = baseMessageSchema
  .extend({
    type: z.literal(MatchmakingMessageType.MATCHMAKER_ERROR),
  })
  .merge(matchmakerErrorPayloadSchema);

const matchmakerStateUpdateSchema = baseMessageSchema
  .extend({
    type: z.literal(MatchmakingMessageType.MATCHMAKER_STATE_UPDATE),
  })
  .merge(matchmakerStateUpdatePayloadSchema);

const matchmakerHostMatchFoundSchema = baseMessageSchema
  .extend({
    type: z.literal(MatchmakingMessageType.MATCHMAKER_HOST_MATCH_FOUND),
  })
  .merge(matchmakerHostMatchFoundPayloadSchema);

// Soft kick activation message schema
const matchmakerActivateSoftKickSchema = baseMessageSchema
  .extend({
    type: z.literal(MatchmakingMessageType.MATCHMAKER_ACTIVATE_SOFT_KICK_REPLY),
  })
  .merge(matchmakerActivateSoftKickPayloadSchema);

// Soft kick activation by session ID message schema
const matchmakerActivateSoftKickBySessionIdSchema = baseMessageSchema
  .extend({
    type: z.literal(MatchmakingMessageType.MATCHMAKER_ACTIVATE_SOFT_KICK_BY_SESSION_ID),
  })
  .merge(matchmakerActivateSoftKickBySessionIdPayloadSchema);

// Campaign switch message schema
const matchmakerCampaignSwitchSchema = baseMessageSchema
  .extend({
    type: z.literal(MatchmakingMessageType.MATCHMAKER_CAMPAIGN_SWITCH),
  })
  .merge(matchmakerCampaignSwitchPayloadSchema);

// Campaign switch failure message schema
const matchmakerCampaignSwitchFailureSchema = baseMessageSchema
  .extend({
    type: z.literal(MatchmakingMessageType.MATCHMAKER_CAMPAIGN_SWITCH_FAILURE),
  })
  .merge(matchmakerCampaignSwitchFailurePayloadSchema);

// Full message type
export const matchmakingMessageSchema = z.discriminatedUnion('type', [
  matchmakerHostConfigSchema,
  matchmakerCreateInvitationSchema,
  matchmakerJoinInvitationSchema,
  matchmakerLeaveInvitationSchema,
  matchmakerUpdatePreferencesSchema,
  matchmakerAddToQueueSchema,
  matchmakerUpdateInvitationSchema,
  matchmakerKickPlayerSchema,
  matchmakerInvalidateSessionSchema,
  matchmakerActivateSoftKickSchema,
  matchmakerActivateSoftKickBySessionIdSchema,
  matchmakerInvitationCreatedSchema,
  matchmakerInvitationUpdatedSchema,
  matchmakerMatchFoundSchema,
  matchmakerMatchReadySchema,
  matchmakerMatchFailedSchema,
  matchmakerQueueStatusSchema,
  matchmakerPlayerKickedSchema,
  matchmakerHostReadySchema,
  matchmakerConnectionInfoSchema,
  matchmakerErrorSchema,
  matchmakerStateUpdateSchema,
  matchmakerHostMatchFoundSchema,
  matchmakerCampaignSwitchSchema,
  matchmakerCampaignSwitchFailureSchema,
]);

// Export payload types
export type MatchmakerHostConfigPayload = z.infer<typeof matchmakerHostConfigPayloadSchema>;
export type MatchmakerCreateInvitationPayload = z.infer<typeof matchmakerCreateInvitationPayloadSchema>;
export type MatchmakerJoinInvitationPayload = z.infer<typeof matchmakerJoinInvitationPayloadSchema>;
export type MatchmakerLeaveInvitationPayload = z.infer<typeof matchmakerLeaveInvitationPayloadSchema>;
export type MatchmakerUpdatePreferencesPayload = z.infer<typeof matchmakerUpdatePreferencesPayloadSchema>;
export type MatchmakerAddToQueuePayload = z.infer<typeof matchmakerAddToQueuePayloadSchema>;
export type MatchmakerInvitationCreatedPayload = z.infer<typeof matchmakerInvitationCreatedPayloadSchema>;
export type MatchmakerInvitationUpdatedPayload = z.infer<typeof matchmakerInvitationUpdatedPayloadSchema>;
export type MatchmakerMatchFoundPayload = z.infer<typeof matchmakerMatchFoundPayloadSchema>;
export type MatchmakerMatchReadyPayload = z.infer<typeof matchmakerMatchReadyPayloadSchema>;
export type MatchmakerMatchFailedPayload = z.infer<typeof matchmakerMatchFailedPayloadSchema>;
export type MatchmakerQueueStatusPayload = z.infer<typeof matchmakerQueueStatusPayloadSchema>;
export type MatchmakerHostReadyPayload = z.infer<typeof matchmakerHostReadyPayloadSchema>;
export type MatchmakerConnectionInfoPayload = z.infer<typeof matchmakerConnectionInfoPayloadSchema>;
export type MatchmakerErrorPayload = z.infer<typeof matchmakerErrorPayloadSchema>;
export type MatchmakerStateUpdatePayload = z.infer<typeof matchmakerStateUpdatePayloadSchema>;
export type MatchmakerHostMatchFoundPayload = z.infer<typeof matchmakerHostMatchFoundPayloadSchema>;
export type MatchmakerUpdateInvitationPayload = z.infer<typeof matchmakerUpdateInvitationPayloadSchema>;
export type MatchmakerKickPlayerPayload = z.infer<typeof matchmakerKickPlayerPayloadSchema>;
export type MatchmakerInvalidateSessionPayload = z.infer<typeof matchmakerInvalidateSessionPayloadSchema>;
export type MatchmakerPlayerKickedPayload = z.infer<typeof matchmakerPlayerKickedPayloadSchema>;

// Soft kick activation payload type
export type MatchmakerActivateSoftKickPayload = z.infer<typeof matchmakerActivateSoftKickPayloadSchema>;
export type MatchmakerActivateSoftKickBySessionIdPayload = z.infer<typeof matchmakerActivateSoftKickBySessionIdPayloadSchema>;

// Campaign switch payload types
export type MatchmakerCampaignSwitchPayload = z.infer<typeof matchmakerCampaignSwitchPayloadSchema>;
export type MatchmakerCampaignSwitchFailurePayload = z.infer<typeof matchmakerCampaignSwitchFailurePayloadSchema>;

// Common types
export type HostOperationalState = z.infer<typeof hostOperationalStateSchema>;
export type HostOrchestrationState = z.infer<typeof hostOrchestrationStateSchema>;
export type HostMatchmakingState = z.infer<typeof hostMatchmakingStateSchema>;
export type HostGameState = z.infer<typeof hostGameStateSchema>;
export type HostCoords = z.infer<typeof hostCoordsSchema>;
export type MatchmakingPreferences = z.infer<typeof matchmakingPreferencesSchema>;
export type MatchmakingPlayer = z.infer<typeof matchmakingPlayerSchema>;

// Full message type
export type MatchmakingMessage = z.infer<typeof matchmakingMessageSchema>;

// PayloadType mapping
type PayloadType<T extends MatchmakingMessageType> = T extends MatchmakingMessageType.MATCHMAKER_HOST_CONFIG
  ? MatchmakerHostConfigPayload
  : T extends MatchmakingMessageType.MATCHMAKER_CREATE_INVITATION
    ? MatchmakerCreateInvitationPayload
    : T extends MatchmakingMessageType.MATCHMAKER_JOIN_INVITATION
      ? MatchmakerJoinInvitationPayload
      : T extends MatchmakingMessageType.MATCHMAKER_LEAVE_INVITATION
        ? MatchmakerLeaveInvitationPayload
        : T extends MatchmakingMessageType.MATCHMAKER_UPDATE_PREFERENCES
          ? MatchmakerUpdatePreferencesPayload
          : T extends MatchmakingMessageType.MATCHMAKER_ADD_TO_QUEUE
            ? MatchmakerAddToQueuePayload
            : T extends MatchmakingMessageType.MATCHMAKER_UPDATE_INVITATION
              ? MatchmakerUpdateInvitationPayload
              : T extends MatchmakingMessageType.MATCHMAKER_KICK_PLAYER
                ? MatchmakerKickPlayerPayload
                : T extends MatchmakingMessageType.MATCHMAKER_INVALIDATE_SESSION
                  ? MatchmakerInvalidateSessionPayload
                  : T extends MatchmakingMessageType.MATCHMAKER_ACTIVATE_SOFT_KICK_REPLY
                    ? MatchmakerActivateSoftKickPayload
                    : T extends MatchmakingMessageType.MATCHMAKER_ACTIVATE_SOFT_KICK_BY_SESSION_ID
                      ? MatchmakerActivateSoftKickBySessionIdPayload
                      : T extends MatchmakingMessageType.MATCHMAKER_INVITATION_CREATED
                        ? MatchmakerInvitationCreatedPayload
                        : T extends MatchmakingMessageType.MATCHMAKER_INVITATION_UPDATED
                          ? MatchmakerInvitationUpdatedPayload
                          : T extends MatchmakingMessageType.MATCHMAKER_MATCH_FOUND
                            ? MatchmakerMatchFoundPayload
                            : T extends MatchmakingMessageType.MATCHMAKER_MATCH_READY
                              ? MatchmakerMatchReadyPayload
                              : T extends MatchmakingMessageType.MATCHMAKER_MATCH_FAILED
                                ? MatchmakerMatchFailedPayload
                                : T extends MatchmakingMessageType.MATCHMAKER_QUEUE_STATUS
                                  ? MatchmakerQueueStatusPayload
                                  : T extends MatchmakingMessageType.MATCHMAKER_PLAYER_KICKED
                                    ? MatchmakerPlayerKickedPayload
                                    : T extends MatchmakingMessageType.MATCHMAKER_HOST_READY
                                      ? MatchmakerHostReadyPayload
                                      : T extends MatchmakingMessageType.MATCHMAKER_CONNECTION_INFO
                                        ? MatchmakerConnectionInfoPayload
                                        : T extends MatchmakingMessageType.MATCHMAKER_ERROR
                                          ? MatchmakerErrorPayload
                                          : T extends MatchmakingMessageType.MATCHMAKER_STATE_UPDATE
                                            ? MatchmakerStateUpdatePayload
                                            : T extends MatchmakingMessageType.MATCHMAKER_HOST_MATCH_FOUND
                                              ? MatchmakerHostMatchFoundPayload
                                              : T extends MatchmakingMessageType.MATCHMAKER_CAMPAIGN_SWITCH
                                                ? MatchmakerCampaignSwitchPayload
                                                : T extends MatchmakingMessageType.MATCHMAKER_CAMPAIGN_SWITCH_FAILURE
                                                  ? MatchmakerCampaignSwitchFailurePayload
                                                  : never;

/**
 * Generate a matchmaking message with the correct type and payload
 */
export function generateMatchmakingMessage<T extends MatchmakingMessageType>(type: T, payload: PayloadType<T>): MatchmakingMessage {
  const base = {
    id: createMessageId(),
    timestamp: Date.now(),
    type,
  };

  switch (type) {
    case MatchmakingMessageType.MATCHMAKER_HOST_CONFIG:
      return matchmakerHostConfigSchema.parse({
        ...base,
        ...payload,
      });
    case MatchmakingMessageType.MATCHMAKER_CREATE_INVITATION:
      return matchmakerCreateInvitationSchema.parse({
        ...base,
        ...payload,
      });
    case MatchmakingMessageType.MATCHMAKER_JOIN_INVITATION:
      return matchmakerJoinInvitationSchema.parse({
        ...base,
        ...payload,
      });
    case MatchmakingMessageType.MATCHMAKER_LEAVE_INVITATION:
      return matchmakerLeaveInvitationSchema.parse({
        ...base,
        ...payload,
      });
    case MatchmakingMessageType.MATCHMAKER_UPDATE_PREFERENCES:
      return matchmakerUpdatePreferencesSchema.parse({
        ...base,
        ...payload,
      });
    case MatchmakingMessageType.MATCHMAKER_ADD_TO_QUEUE:
      return matchmakerAddToQueueSchema.parse({
        ...base,
        ...payload,
      });
    case MatchmakingMessageType.MATCHMAKER_UPDATE_INVITATION:
      return matchmakerUpdateInvitationSchema.parse({
        ...base,
        ...payload,
      });
    case MatchmakingMessageType.MATCHMAKER_INVITATION_CREATED:
      return matchmakerInvitationCreatedSchema.parse({
        ...base,
        ...payload,
      });
    case MatchmakingMessageType.MATCHMAKER_INVITATION_UPDATED:
      return matchmakerInvitationUpdatedSchema.parse({
        ...base,
        ...payload,
      });
    case MatchmakingMessageType.MATCHMAKER_MATCH_FOUND:
      return matchmakerMatchFoundSchema.parse({
        ...base,
        ...payload,
      });
    case MatchmakingMessageType.MATCHMAKER_MATCH_READY:
      return matchmakerMatchReadySchema.parse({
        ...base,
        ...payload,
      });
    case MatchmakingMessageType.MATCHMAKER_MATCH_FAILED:
      return matchmakerMatchFailedSchema.parse({
        ...base,
        ...payload,
      });
    case MatchmakingMessageType.MATCHMAKER_QUEUE_STATUS:
      return matchmakerQueueStatusSchema.parse({
        ...base,
        ...payload,
      });
    case MatchmakingMessageType.MATCHMAKER_HOST_READY:
      return matchmakerHostReadySchema.parse({
        ...base,
        ...payload,
      });
    case MatchmakingMessageType.MATCHMAKER_CONNECTION_INFO:
      return matchmakerConnectionInfoSchema.parse({
        ...base,
        ...payload,
      });
    case MatchmakingMessageType.MATCHMAKER_ERROR:
      return matchmakerErrorSchema.parse({
        ...base,
        ...payload,
      });
    case MatchmakingMessageType.MATCHMAKER_STATE_UPDATE:
      return matchmakerStateUpdateSchema.parse({
        ...base,
        ...payload,
      });
    case MatchmakingMessageType.MATCHMAKER_HOST_MATCH_FOUND:
      return matchmakerHostMatchFoundSchema.parse({
        ...base,
        ...payload,
      });
    case MatchmakingMessageType.MATCHMAKER_KICK_PLAYER:
      return matchmakerKickPlayerSchema.parse({
        ...base,
        ...payload,
      });
    case MatchmakingMessageType.MATCHMAKER_PLAYER_KICKED:
      return matchmakerPlayerKickedSchema.parse({
        ...base,
        ...payload,
      });
    case MatchmakingMessageType.MATCHMAKER_INVALIDATE_SESSION:
      return matchmakerInvalidateSessionSchema.parse({
        ...base,
        ...payload,
      });
    case MatchmakingMessageType.MATCHMAKER_ACTIVATE_SOFT_KICK_REPLY:
      return matchmakerActivateSoftKickSchema.parse({
        ...base,
        ...payload,
      });
    case MatchmakingMessageType.MATCHMAKER_ACTIVATE_SOFT_KICK_BY_SESSION_ID:
      return matchmakerActivateSoftKickBySessionIdSchema.parse({
        ...base,
        ...payload,
      });
    case MatchmakingMessageType.MATCHMAKER_CAMPAIGN_SWITCH:
      return matchmakerCampaignSwitchSchema.parse({
        ...base,
        ...payload,
      });
    case MatchmakingMessageType.MATCHMAKER_CAMPAIGN_SWITCH_FAILURE:
      return matchmakerCampaignSwitchFailureSchema.parse({
        ...base,
        ...payload,
      });
    default: {
      const exhaustiveCheck: never = type;
      throw new Error(`Unhandled message type: ${exhaustiveCheck}`);
    }
  }
}
